import React, { useState, useEffect } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import Header from "../components/Header";
import Footer from "../components/Footer";
import SEO from "../components/SEO";
import { useAuthContext } from "../contexts/AuthContext";
import SupervisorDashboard from "./SupervisorDashboard";
import SupervisorInvitations from "./SupervisorInvitations";
import SupervisorStudentDetail from "./SupervisorStudentDetail";
import styles from "./Supervisor.module.css";
import { authenticatedFetch } from "../utils/authenticatedFetch";

// SVG Icons
const DashboardIcon = () => (
    <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <rect x="3" y="3" width="7" height="7" />
        <rect x="14" y="3" width="7" height="7" />
        <rect x="14" y="14" width="7" height="7" />
        <rect x="3" y="14" width="7" height="7" />
    </svg>
);

const InvitationsIcon = () => (
    <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z" />
        <polyline points="22,6 12,13 2,6" />
    </svg>
);

const SeatsIcon = () => (
    <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" />
        <circle cx="9" cy="7" r="4" />
        <path d="M23 21v-2a4 4 0 0 0-3-3.87" />
        <path d="M16 3.13a4 4 0 0 1 0 7.75" />
    </svg>
);

type Tab = "dashboard" | "invitations";

const Supervisor: React.FC = () => {
    const { user, loading: authLoading } = useAuthContext();
    const location = useLocation();
    const navigate = useNavigate();
    const params = useParams<{ studentId?: string }>();
    const [isSupervisor, setIsSupervisor] = useState<boolean | null>(null);
    const [checkingRole, setCheckingRole] = useState(false);
    const [seats, setSeats] = useState<{ used: number; max: number; remaining: number } | null>(null);

    const apiUrl = process.env.REACT_APP_API_URL || "http://localhost:5001";

    // Determine current view
    const isStudentDetail = !!params.studentId;
    const currentTab: Tab = location.pathname.includes("/supervisor/invitations") ? "invitations" : "dashboard";

    const navItems: { label: string; tab: Tab; path: string; icon: React.ReactNode }[] = [
        { label: "Dashboard", tab: "dashboard", path: "/supervisor", icon: <DashboardIcon /> },
        { label: "Invitations", tab: "invitations", path: "/supervisor/invitations", icon: <InvitationsIcon /> },
    ];

    // Check supervisor role
    useEffect(() => {
        if (authLoading) return;
        if (isSupervisor) return;

        const checkRole = async () => {
            if (!user?.id) {
                navigate("/login", { state: { from: location.pathname } });
                return;
            }
            setCheckingRole(true);
            try {
                const res = await authenticatedFetch(`${apiUrl}/api/auth/is-supervisor/${user.id}`);
                if (res.ok) {
                    const data = await res.json();
                    if (!data.is_supervisor) {
                        navigate("/");
                        return;
                    }
                    setIsSupervisor(true);
                } else {
                    navigate("/");
                }
            } catch (err) {
                console.error("Failed to check supervisor status", err);
                navigate("/");
            } finally {
                setCheckingRole(false);
            }
        };

        checkRole();
    }, [user, authLoading, navigate, location.pathname, apiUrl, isSupervisor]);

    // Fetch seat usage
    useEffect(() => {
        if (!isSupervisor || !user?.id) return;
        const fetchSeats = async () => {
            try {
                const res = await authenticatedFetch(`${apiUrl}/api/supervisor/seats?supervisor_id=${user.id}`);
                if (res.ok) {
                    setSeats(await res.json());
                }
            } catch (err) {
                console.error("Failed to fetch seats", err);
            }
        };
        fetchSeats();
    }, [isSupervisor, user?.id, apiUrl]);

    const refreshSeats = async () => {
        if (!user?.id) return;
        try {
            const res = await authenticatedFetch(`${apiUrl}/api/supervisor/seats?supervisor_id=${user.id}`);
            if (res.ok) {
                setSeats(await res.json());
            }
        } catch {}
    };

    // Loading state
    if (authLoading || checkingRole || isSupervisor === null) {
        return (
            <div className={styles.pageContainer}>
                <SEO title="Supervisor | C-Shaped" />
                <Header />
                <main className={styles.mainContent}>
                    <div className={styles.loadingContainer}>
                        <div className={styles.loadingSpinner} />
                        <p>Verifying access...</p>
                    </div>
                </main>
                <Footer />
            </div>
        );
    }

    if (!isSupervisor) return null;

    return (
        <div className={styles.pageContainer}>
            <SEO title="Supervisor | C-Shaped" />
            <Header />

            <main className={styles.mainContent}>
                <div className={styles.supervisorLayout}>
                    {/* Sidebar */}
                    <aside className={styles.sidebar}>
                        <h2 className={styles.sidebarTitle}>Supervisor</h2>
                        <nav className={styles.sidebarNav}>
                            {navItems.map((item) => (
                                <button
                                    key={item.tab}
                                    onClick={() => navigate(item.path)}
                                    className={`${styles.navItem} ${
                                        !isStudentDetail && currentTab === item.tab ? styles.navItemActive : ""
                                    }`}
                                >
                                    <span className={styles.navIcon}>{item.icon}</span>
                                    {item.label}
                                </button>
                            ))}
                        </nav>
                        <div className={styles.sidebarDivider} />
                        <div className={styles.seatInfo}>
                            <SeatsIcon />
                            Seats: <span className={styles.seatCount}>{seats ? `${seats.used}/${seats.max}` : "..."}</span>
                        </div>
                    </aside>

                    {/* Content */}
                    <div className={styles.content}>
                        {isStudentDetail ? (
                            <SupervisorStudentDetail
                                studentId={params.studentId!}
                                supervisorId={user!.id}
                                onBack={() => navigate("/supervisor")}
                                onStudentRemoved={refreshSeats}
                            />
                        ) : currentTab === "dashboard" ? (
                            <SupervisorDashboard
                                supervisorId={user!.id}
                                onStudentClick={(studentId) => navigate(`/supervisor/student/${studentId}`)}
                                onStudentRemoved={refreshSeats}
                            />
                        ) : (
                            <SupervisorInvitations
                                supervisorId={user!.id}
                                onInvitationChange={refreshSeats}
                            />
                        )}
                    </div>
                </div>
            </main>

            <Footer />
        </div>
    );
};

export default Supervisor;
